import { KnownSettingsNodeDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback } from 'react';
import { Snackbar } from '../../ui/components/pure/snackbar/snackbar.component';
import { DeleteProjectModal } from './actions-components/delete-project-modal/delete-project.modal';
import { DeleteProjectModuleModal } from './actions-components/delete-project-module-modal/delete-project-module.modal';
import { DeleteHubModal } from './actions-components/delete-hub-modal/delete-hub.modal';
import { DeleteAccessControlModal } from './actions-components/delete-access-control-modal/delete-access-control.modal';
import { DeleteRegistrationPageModal } from './actions-components/delete-registration-page-model/delete-registration-page.modal';
import { OpenRegistrationPage } from './actions-components/open-registration-page/open-registration-page.component';
import { HubAccessedViaModal } from './actions-components/hub-accessed-via-modal/hub-accessed-via.modal';
import { AddModulesModal } from './actions-components/add-modules-modal/add-modules.modal';
import { ApplyKioskThemePresetModal } from './actions-components/apply-kiosk-theme-preset-modal/apply-kiosk-theme-preset.modal';
import { useSettingsTreeReducerContext } from './reducer/settings-tree-reducer.context';
import { RemoveHubRelationshipModal } from './actions-components/remove-hub-relationship-modal/remove-hub-relationship.modal';
import { RelationshipType, SettingsTreeDataRequestType } from './reducer/settings-tree-reducer.types';
import { AddCarrierModal } from '../carriers/add-carrier-modal/add-carrier.modal';
import { AddRegionModal } from '../carriers/add-region-modal/add-region.modal';
import { AssignNetworksModal } from '../carriers/assign-networks-modal/assign-networks.modal';
import { DeleteCarrierModal } from '../carriers/actions/delete-carrier.modal';

export function SettingsTreeActionsObserver() {
  const { state, dispatch } = useSettingsTreeReducerContext();

  const onClose = useCallback(() => {
    dispatch({
      type: 'CLEAR_CURRENT_ACTION'
    });
  }, [dispatch]);

  if (!state.currentAction) {
    return null;
  }

  switch (state.currentAction.type) {
    case 'DELETE_NODE':
      switch (state.currentAction.nodeType) {
        case KnownSettingsNodeDtoType.Project:
          return (
            <DeleteProjectModal
              projectId={state.currentAction.entityId}
              hasChildProjects={state.currentAction.childNodeIds.length > 0}
              onSuccess={() => {
                dispatch({
                  type: 'SET_CURRENT_ACTION',
                  payload: {
                    type: 'DELETE_NODE_SUCCESS'
                  }
                });
              }}
              onCancel={onClose}
            />
          );
        case KnownSettingsNodeDtoType.ProjectModule:
          // When a project module is deleted, the projectId should be present.
          if (!state.currentAction.projectId) {
            return null;
          }
          return (
            <DeleteProjectModuleModal
              projectId={state.currentAction.projectId}
              projectModuleId={state.currentAction.entityId}
              onSuccess={() => {
                dispatch({
                  type: 'SET_CURRENT_ACTION',
                  payload: {
                    type: 'DELETE_NODE_SUCCESS'
                  }
                });
              }}
              onCancel={onClose}
            />
          );
        case KnownSettingsNodeDtoType.Hub:
          return (
            <DeleteHubModal
              hubId={state.currentAction.entityId}
              hubName={state.currentAction.label}
              onSuccess={() => {
                dispatch({
                  type: 'SET_CURRENT_ACTION',
                  payload: { type: 'DELETE_NODE_SUCCESS' }
                });
              }}
              onCancel={onClose}
            />
          );
        case KnownSettingsNodeDtoType.Group:
          return (
            <DeleteCarrierModal
              carrier={{ id: state.currentAction.entityId, name: state.currentAction.label ?? '' }}
              onSuccess={() => {
                dispatch({
                  type: 'SET_CURRENT_ACTION',
                  payload: { type: 'DELETE_NODE_SUCCESS' }
                });
              }}
              onCancel={onClose}
            />
          );
        case KnownSettingsNodeDtoType.AccessControl:
          return (
            <DeleteAccessControlModal
              accessControlId={state.currentAction.entityId}
              label={state.currentAction.label ?? state.currentAction.entityId}
              onSuccess={() => {
                dispatch({
                  type: 'SET_CURRENT_ACTION',
                  payload: { type: 'DELETE_NODE_SUCCESS' }
                });
              }}
              onCancel={onClose}
            />
          );
        case KnownSettingsNodeDtoType.RegistrationPage:
          // When a registration page is deleted, the projectId should be present.
          if (!state.currentAction.projectId) {
            return null;
          }
          return (
            <DeleteRegistrationPageModal
              projectId={state.currentAction.projectId}
              registrationPageId={state.currentAction.entityId}
              onSuccess={() => {
                dispatch({
                  type: 'SET_CURRENT_ACTION',
                  payload: {
                    type: 'DELETE_NODE_SUCCESS'
                  }
                });
              }}
              onCancel={onClose}
            />
          );
        default:
          return null;
      }
    case 'DELETE_RELATIONSHIP':
      if (!state.currentAction.relatedEntityId || state.currentAction.relationshipType === RelationshipType.Unknown) {
        return null;
      }
      return (
        <RemoveHubRelationshipModal
          hubId={state.currentAction.relatedEntityId}
          projectId={state.currentAction.selectedEntityId}
          relationship={state.currentAction.relationshipType}
          onSuccess={() => {
            dispatch({
              type: 'SET_CURRENT_ACTION',
              payload: {
                type: 'DELETE_NODE_SUCCESS'
              }
            });
          }}
          onCancel={onClose}
        />
      );
    case 'DELETE_NODE_SUCCESS':
      return (
        <Snackbar severity="success" onClose={onClose}>
          Successfully deleted
        </Snackbar>
      );
    case 'OPEN_REGISTRATION_PAGE':
      return (
        <OpenRegistrationPage
          projectId={state.currentAction.projectId}
          registrationPageId={state.currentAction.registrationPageId}
          onDone={onClose}
        />
      );
    case 'HUB_CREATED':
      return (
        <HubAccessedViaModal
          hubId={state.currentAction.hubId}
          hubName={state.currentAction.hubName}
          managedByProjectId={state.currentAction.managedByProjectId}
          onDone={onClose}
        />
      );
    case 'PROJECT_CREATED':
      return <AddModulesModal onDone={onClose} />;
    case 'ADD_CARRIER': {
      const { parentProjectId, parentNodeId } = state.currentAction;
      return (
        <AddCarrierModal
          parentProjectId={parentProjectId}
          onSuccess={(carrierProjectId, carrierName) => {
            // The tree returns the whole subtree, so the new project appears once it refetches.
            dispatch({
              type: 'ENQUEUE_NODE_SELECTION',
              payload: {
                entityId: carrierProjectId,
                parentNodeId,
                requestType: SettingsTreeDataRequestType.Projects,
                expandNode: true
              }
            });
            dispatch({
              type: 'SET_CURRENT_ACTION',
              payload: { type: 'CARRIER_PROJECT_CREATED', name: carrierName }
            });
          }}
          onCancel={onClose}
        />
      );
    }
    case 'ASSIGN_NETWORKS': {
      const { organisationGroupId, carrierName } = state.currentAction;
      return (
        <AssignNetworksModal
          groupID={organisationGroupId}
          carrierName={carrierName}
          onSuccess={onClose}
          onCancel={onClose}
        />
      );
    }
    case 'ADD_REGION': {
      const { carrierProjectId, carrierProjectName, parentNodeId } = state.currentAction;
      return (
        <AddRegionModal
          carrierProjectId={carrierProjectId}
          carrierName={carrierProjectName}
          onSuccess={(regionProjectId, regionName) => {
            dispatch({
              type: 'ENQUEUE_NODE_SELECTION',
              payload: {
                entityId: regionProjectId,
                parentNodeId,
                requestType: SettingsTreeDataRequestType.Projects,
                expandNode: true
              }
            });
            dispatch({
              type: 'SET_CURRENT_ACTION',
              payload: { type: 'CARRIER_PROJECT_CREATED', name: regionName }
            });
          }}
          onCancel={onClose}
        />
      );
    }
    case 'CARRIER_PROJECT_CREATED':
      return (
        <Snackbar severity="success" onClose={onClose}>
          {state.currentAction.name} was created
        </Snackbar>
      );
    case 'APPLY_KIOSK_THEME_PRESET': {
      const { nodeId, kioskId, projectId } = state.currentAction;
      return (
        <ApplyKioskThemePresetModal
          kioskId={kioskId}
          projectId={projectId}
          onSuccess={() => {
            dispatch({ type: 'RESET_FORM', payload: { nodeId } });
            onClose();
          }}
          onCancel={onClose}
        />
      );
    }
    default:
      return null;
  }
}
